// Personal Forecast Service - Database operations for scenarios
// Saves/loads forecast scenarios from backend

import { ForecastScenario, DEFAULT_FORECAST_SETTINGS } from '../types/personalForecast';
import authService from './auth-service';
import { buildApiUrl } from '../config/api';

const API_BASE_URL = buildApiUrl('');

class PersonalForecastService {
  async getUserForecasts(): Promise<ForecastScenario[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/scenarios/?type=personal-finance`, {
        headers: authService.getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch forecast scenarios');
      const scenarios = await response.json();
      // The backend returns rows in table order. Newest first, so callers can take [0].
      return scenarios
        .map((s: any) => this.convertFromBackend(s))
        .sort((a: ForecastScenario, b: ForecastScenario) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
    } catch (error) {
      console.error('Error fetching forecast scenarios:', error);
      return [];
    }
  }

  async saveForecast(scenario: ForecastScenario): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/scenarios/`, {
        method: 'POST',
        // Flask reads the body only when it is declared as JSON; without this header every save returned 400.
        headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
        body: JSON.stringify({
          name: scenario.name,
          description: '',
          scenario_type: 'personal-finance',
          scenario_data: this.convertToBackend(scenario)
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save forecast');
      }
      const data = await response.json();
      return data.id.toString();
    } catch (error) {
      console.error('Error saving forecast:', error);
      throw error;
    }
  }

  async updateForecast(id: string, scenario: ForecastScenario): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/scenarios/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
        body: JSON.stringify({
          name: scenario.name,
          description: '',
          scenario_type: 'personal-finance',
          scenario_data: this.convertToBackend(scenario)
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update forecast');
      }
    } catch (error) {
      console.error('Error updating forecast:', error);
      throw error;
    }
  }

  async deleteForecast(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/scenarios/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete forecast');
    } catch (error) {
      console.error('Error deleting forecast:', error);
      throw error;
    }
  }

  /**
   * Convert backend scenario to new ForecastScenario format.
   * Detects old wizard format (has income[]) and returns it as-is
   * so PersonalForecast.tsx can detect and skip migration.
   */
  private convertFromBackend(backendScenario: any): ForecastScenario {
    const data = backendScenario.scenario_data || {};

    // Old wizard format detection — return with income[] so caller can detect
    if (data.income && !data.cashFlows) {
      return {
        id: backendScenario.id.toString(),
        name: backendScenario.name,
        accounts: data.accounts || [],
        cashFlows: [],
        fxRates: [],
        settings: { ...DEFAULT_FORECAST_SETTINGS },
        createdAt: backendScenario.created_at,
        updatedAt: backendScenario.updated_at || backendScenario.created_at,
        // Stash old data so caller can detect
        ...(data.income ? { income: data.income } : {}),
      } as any;
    }

    // New format
    return {
      id: backendScenario.id.toString(),
      name: backendScenario.name,
      accounts: data.accounts || [],
      cashFlows: data.cashFlows || [],
      fxRates: data.fxRates || [],
      settings: { ...DEFAULT_FORECAST_SETTINGS, ...(data.settings || {}) },
      createdAt: backendScenario.created_at,
      updatedAt: backendScenario.updated_at || backendScenario.created_at,
    };
  }

  private convertToBackend(scenario: ForecastScenario): any {
    return {
      accounts: scenario.accounts,
      cashFlows: scenario.cashFlows,
      fxRates: scenario.fxRates,
      settings: scenario.settings,
    };
  }

  saveToLocalStorage(scenario: ForecastScenario): void {
    try {
      const scenarios = this.getFromLocalStorage();
      const index = scenarios.findIndex(s => s.id === scenario.id);
      if (index >= 0) {
        scenarios[index] = scenario;
      } else {
        scenarios.push(scenario);
      }
      localStorage.setItem('larryshelp_personal_forecasts', JSON.stringify(scenarios));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  getFromLocalStorage(): ForecastScenario[] {
    try {
      const data = localStorage.getItem('larryshelp_personal_forecasts');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return [];
    }
  }

  deleteFromLocalStorage(id: string): void {
    try {
      const scenarios = this.getFromLocalStorage();
      const filtered = scenarios.filter(s => s.id !== id);
      localStorage.setItem('larryshelp_personal_forecasts', JSON.stringify(filtered));
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
    }
  }
}

export default new PersonalForecastService();
